import { Campaign } from "@mui/icons-material";
import {
  Box,
  Card,
  Container,
  Divider,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ReactComponent as NothingFoundIcon } from "assets/nothing_found.svg";
import CampaignCard from "modules/Campaigns/components/CampaignCard.component";
import Drawer from "modules/UI/components/Drawer.component";
import { useActiveUser } from "modules/User/activeUser";
import React from "react";
import { useMatch, useSearchParams } from "react-router-dom";
import { Search } from "@mui/icons-material";
import CampaignsService from "../service/campaigns.service";
import { CampaignState } from "../service/campaigns.types";
import CreateCampaignContainer from "./create.container";
import { useUserPermissions } from "modules/User/service/userPermissions.hook";



export const CampaignsListContainer = () => {
  const { data: userPermissions, isLoading } = useUserPermissions();
  // Extract permission names from API response
  const permissionNames =
    userPermissions?.permissions?.map((permission) => permission.name) ?? [];

  // Extract allowed order window names from API response
  const allowedOrderWindowNames =
    userPermissions?.allowedOrderWindows?.map((orderWindow) => orderWindow.name) ?? [];

  // Check whether a specific permission exists
  const hasPermission = (permissionName: string): boolean =>
    permissionNames.includes(permissionName);

  const hasAllowedOrderWindow = (state: CampaignState): boolean =>
    allowedOrderWindowNames.includes(CampaignState[state]);

  const currentUser = useActiveUser();

  let [searchParams, setSearchParams] = useSearchParams();
  const isPreview = useMatch("/campaigns/create/new");
  const openNewDrawer = !!isPreview;

  const [createDialogOpen, setCreateDialogOpen] = React.useState(openNewDrawer);
  const [activeTab, setActiveTab] = React.useState(0);
  // const [activeTab, setActiveTab] = React.useState(defaultTab);

  const [globalFilter, setGlobalFilter] = React.useState(
    searchParams.get("searchTerm") || ""
  );

  const [selectedPendingState, setSelectedPendingState] =
    React.useState<CampaignState | null>(() => {
      if (hasAllowedOrderWindow(CampaignState.Pending_NPD)) {
        return CampaignState.Pending_NPD;
      }

      if (hasAllowedOrderWindow(CampaignState.Pending_Rejected_NPD)) {
        return CampaignState.Pending_Rejected_NPD;
      }

      if (hasAllowedOrderWindow(CampaignState.Pending_GlobalQuality)) {
        return CampaignState.Pending_GlobalQuality;
      }

      if (
        hasAllowedOrderWindow(
          CampaignState.Pending_Rejected_GlobalQuality
        )
      ) {
        return CampaignState.Pending_Rejected_GlobalQuality;
      }

      if (hasAllowedOrderWindow(CampaignState.Pending_Market_Proc)) {
        return CampaignState.Pending_Market_Proc;
      }

      if (hasAllowedOrderWindow(CampaignState.Pending_Confirm_Price)) {
        return CampaignState.Pending_Confirm_Price;
      }

      return null;
    });

  const [activePagination, setActivePagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  // const [futurePagination, setFuturePagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [pastPagination, setPastPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [draftPagination, setDraftPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [archivedPagination, setArchivedPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [pendingNpdPagination, setPendingNpdPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [pendingGlobalQualityPagination, setPendingGlobalQualityPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [pendingRejectedNpdPagination, setPendingRejectedNpdPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [pendingMarketProcPagination, setPendingMarketProcPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [pendingRejectedGlobalQualityPagination, setPendingRejectedGlobalQualityPagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [pendingConfirmPricePagination, setPendingConfirmPricePagination] = React.useState({ pageIndex: 0, pageSize: 12 });
  const [closedSubmitPrPagination, setClosedSubmitPrPagination] = React.useState({ pageIndex: 0, pageSize: 12 });


  const handleSearch = () => {
    setSearchParams((oldParams) => {
      let params = new URLSearchParams(oldParams);
      if (globalFilter.trim()) {
        params.set("searchTerm", globalFilter.toString());
      } else {
        params.delete("searchTerm");
      }
      return params;
    });
  };

  const searchTerm = searchParams.get("searchTerm") || "";

  const { data: activeCampaigns, refetch: refetchActiveCampaigns } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Active),
    queryKey: ["campaigns/active", searchTerm, activePagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm: searchTerm,
        skip: activePagination.pageIndex * activePagination.pageSize,
        take: activePagination.pageSize,
        state: [CampaignState.Active],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const { data: pastCampaigns, refetch: refetchPastCampaigns } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Past),
    queryKey: ["campaigns/past", searchTerm, pastPagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm: searchTerm,
        skip: pastPagination.pageIndex * pastPagination.pageSize,
        take: pastPagination.pageSize,
        state: [CampaignState.Past],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const { data: draftCampaigns, refetch: refetchDraftCampaigns } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Draft),
    queryKey: ["campaigns/draft", searchTerm, draftPagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm,
        skip: draftPagination.pageIndex * draftPagination.pageSize,
        take: draftPagination.pageSize,
        state: [CampaignState.Draft],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const { data: archivedCampaigns, refetch: refetchArchivedCampaigns } =
    useQuery({
      enabled: hasAllowedOrderWindow(CampaignState.Archived),
      queryKey: ["campaigns/archived", searchTerm, archivedPagination],
      queryFn: async () => {
        const response = await CampaignsService.getCampaigns({
          searchTerm,
          skip: archivedPagination.pageIndex * archivedPagination.pageSize,
          take: archivedPagination.pageSize,
          state: [CampaignState.Archived],
        });
        return response;
      },
      initialData: { data: [], take: 12, skip: 0, total: 0 },
    });

  const { data: pendingNpdCampaigns, refetch: refetchPendingNpdCampaigns } =
    useQuery({
      enabled: hasAllowedOrderWindow(CampaignState.Pending_NPD),
      queryKey: ["campaigns/pending-npd", searchTerm, pendingNpdPagination],
      queryFn: async () => {
        const response = await CampaignsService.getCampaigns({
          searchTerm,
          skip: pendingNpdPagination.pageIndex * pendingNpdPagination.pageSize,
          take: pendingNpdPagination.pageSize,
          state: [CampaignState.Pending_NPD],
        });
        return response;
      },
      initialData: { data: [], take: 12, skip: 0, total: 0 },
    });

  const {
    data: pendingGlobalQualityCampaigns,
    refetch: refetchPendingGlobalQualityCampaigns,
  } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Pending_GlobalQuality),
    queryKey: ["campaigns/pending-global-quality", searchTerm, pendingGlobalQualityPagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm,
        skip:
          pendingGlobalQualityPagination.pageIndex *
          pendingGlobalQualityPagination.pageSize,
        take: pendingGlobalQualityPagination.pageSize,
        state: [CampaignState.Pending_GlobalQuality],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const {
    data: pendingRejectedNpdCampaigns,
    refetch: refetchPendingRejectedNpdCampaigns,
  } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Pending_Rejected_NPD),
    queryKey: ["campaigns/pending-rejected-npd", searchTerm, pendingRejectedNpdPagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm,
        skip:
          pendingRejectedNpdPagination.pageIndex *
          pendingRejectedNpdPagination.pageSize,
        take: pendingRejectedNpdPagination.pageSize,
        state: [CampaignState.Pending_Rejected_NPD],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const {
    data: pendingMarketProcCampaigns,
    refetch: refetchPendingMarketProcCampaigns,
  } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Pending_Market_Proc),
    queryKey: ["campaigns/pending-market-proc", searchTerm, pendingMarketProcPagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm,
        skip:
          pendingMarketProcPagination.pageIndex *
          pendingMarketProcPagination.pageSize,
        take: pendingMarketProcPagination.pageSize,
        state: [CampaignState.Pending_Market_Proc],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const {
    data: pendingRejectedGlobalQualityCampaigns,
    refetch: refetchPendingRejectedGlobalQualityCampaigns,
  } = useQuery({
    enabled: hasAllowedOrderWindow(
      CampaignState.Pending_Rejected_GlobalQuality
    ),
    queryKey: [
      "campaigns/pending-rejected-global-quality",
      searchTerm,
      pendingRejectedGlobalQualityPagination,
    ],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm,
        skip:
          pendingRejectedGlobalQualityPagination.pageIndex *
          pendingRejectedGlobalQualityPagination.pageSize,
        take: pendingRejectedGlobalQualityPagination.pageSize,
        state: [CampaignState.Pending_Rejected_GlobalQuality],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const { data: pendingConfirmPriceCampaigns } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Pending_Confirm_Price),
    queryKey: ["campaigns/pending-confirm-price", searchTerm, pendingConfirmPricePagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm,
        skip:
          pendingConfirmPricePagination.pageIndex *
          pendingConfirmPricePagination.pageSize,
        take: pendingConfirmPricePagination.pageSize,
        state: [CampaignState.Pending_Confirm_Price],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const {
    data: closedSubmitPrCampaigns,
    refetch: refetchClosedSubmitPrCampaigns,
  } = useQuery({
    enabled: hasAllowedOrderWindow(CampaignState.Closed_Submit_PR),
    queryKey: ["campaigns/closedSubmitPr", searchTerm, closedSubmitPrPagination],
    queryFn: async () => {
      const response = await CampaignsService.getCampaigns({
        searchTerm,
        skip:
          closedSubmitPrPagination.pageIndex *
          closedSubmitPrPagination.pageSize,
        take: closedSubmitPrPagination.pageSize,
        state: [CampaignState.Closed_Submit_PR],
      });
      return response;
    },
    initialData: { data: [], take: 12, skip: 0, total: 0 },
  });

  const getPendingCampaignsData = () => {
    switch (selectedPendingState) {
      case CampaignState.Pending_NPD:
        return { campaigns: pendingNpdCampaigns, pagination: pendingNpdPagination, setPagination: setPendingNpdPagination };
      case CampaignState.Pending_GlobalQuality:
        return { campaigns: pendingGlobalQualityCampaigns, pagination: pendingGlobalQualityPagination, setPagination: setPendingGlobalQualityPagination };
      case CampaignState.Pending_Rejected_NPD:
        return { campaigns: pendingRejectedNpdCampaigns, pagination: pendingRejectedNpdPagination, setPagination: setPendingRejectedNpdPagination };
      case CampaignState.Pending_Market_Proc:
        return { campaigns: pendingMarketProcCampaigns, pagination: pendingMarketProcPagination, setPagination: setPendingMarketProcPagination };
      case CampaignState.Pending_Rejected_GlobalQuality:
        return { campaigns: pendingRejectedGlobalQualityCampaigns, pagination: pendingRejectedGlobalQualityPagination, setPagination: setPendingRejectedGlobalQualityPagination };
      case CampaignState.Pending_Confirm_Price:
        return { campaigns: pendingConfirmPriceCampaigns, pagination: pendingConfirmPricePagination, setPagination: setPendingConfirmPricePagination };
      // case CampaignState.Closed_Submit_PR:
      //   return {
      //     campaigns: closedSubmitPrCampaigns,
      //     pagination: closedSubmitPrPagination,
      //     setPagination: setClosedSubmitPrPagination,
      //   };
      default:
        return null;
    }
  };

  const getPendingTotalCount = () => {
    let total = 0;

    if (hasAllowedOrderWindow(CampaignState.Pending_NPD)) {
      total += pendingNpdCampaigns?.total || 0;
    }

    if (hasAllowedOrderWindow(CampaignState.Pending_GlobalQuality)) {
      total += pendingGlobalQualityCampaigns?.total || 0;
    }

    if (hasAllowedOrderWindow(CampaignState.Pending_Rejected_NPD)) {
      total += pendingRejectedNpdCampaigns?.total || 0;
    }

    if (hasAllowedOrderWindow(CampaignState.Pending_Market_Proc)) {
      total += pendingMarketProcCampaigns?.total || 0;
    }

    if (hasAllowedOrderWindow(CampaignState.Pending_Rejected_GlobalQuality)) {
      total += pendingRejectedGlobalQualityCampaigns?.total || 0;
    }

    if (hasAllowedOrderWindow(CampaignState.Pending_Confirm_Price)) {
      total += pendingConfirmPriceCampaigns?.total || 0;
    }

    // if (hasAllowedOrderWindow(CampaignState.Closed_Submit_PR)) {
    //   total += closedSubmitPrCampaigns?.total || 0;
    // }

    return total;
  };

  const getCurrentTabData = () => {
    const tabData = [
      ...(currentUser.hasAboveMarketRole() &&
        hasAllowedOrderWindow(CampaignState.Draft)
        ? [
          {
            campaigns: draftCampaigns,
            pagination: draftPagination,
            setPagination: setDraftPagination,
          },
        ]
        : []),

      ...(allowedOrderWindowNames.length > 0
        ? [
          getPendingCampaignsData() || {
            campaigns: pendingMarketProcCampaigns,
            pagination: pendingMarketProcPagination,
            setPagination: setPendingMarketProcPagination,
          },
        ]
        : []),

      ...(hasAllowedOrderWindow(CampaignState.Closed_Submit_PR)
        ? [
          {
            campaigns: closedSubmitPrCampaigns,
            pagination: closedSubmitPrPagination,
            setPagination: setClosedSubmitPrPagination,
          },
        ]
        : []),

      ...(currentUser.hasAboveMarketRole() &&
        hasAllowedOrderWindow(CampaignState.Archived)
        ? [
          {
            campaigns: archivedCampaigns,
            pagination: archivedPagination,
            setPagination: setArchivedPagination,
          },
        ]
        : []),
    ];

    return tabData[activeTab];
  };

  const currentTabData = getCurrentTabData();

  const handlePendingStateSelect = (state: CampaignState) => {
    setSelectedPendingState(state);
  };

  const getPendingTabIndex = () => {
    let index = 0;

    if (
      currentUser.hasAboveMarketRole() &&
      hasAllowedOrderWindow(CampaignState.Draft)
    ) {
      index++;
    }

    if (allowedOrderWindowNames.length > 0) {
      return index;
    }

    return -1;
  };

  const pendingTabIndex = getPendingTabIndex();
  React.useEffect(() => {
    if (activeTab === pendingTabIndex && !selectedPendingState) {
      if (hasAllowedOrderWindow(CampaignState.Pending_NPD)) {
        setSelectedPendingState(CampaignState.Pending_NPD);
      } else if (
        hasAllowedOrderWindow(CampaignState.Pending_Rejected_NPD)
      ) {
        setSelectedPendingState(CampaignState.Pending_Rejected_NPD);
      } else if (
        hasAllowedOrderWindow(CampaignState.Pending_GlobalQuality)
      ) {
        setSelectedPendingState(CampaignState.Pending_GlobalQuality);
      } else if (
        hasAllowedOrderWindow(
          CampaignState.Pending_Rejected_GlobalQuality
        )
      ) {
        setSelectedPendingState(
          CampaignState.Pending_Rejected_GlobalQuality
        );
      } else if (
        hasAllowedOrderWindow(CampaignState.Pending_Market_Proc)
      ) {
        setSelectedPendingState(CampaignState.Pending_Market_Proc);
      } else if (
        hasAllowedOrderWindow(CampaignState.Pending_Confirm_Price)
      ) {
        setSelectedPendingState(CampaignState.Pending_Confirm_Price);
      }
    }
  }, [
    activeTab,
    pendingTabIndex,
    selectedPendingState,
    allowedOrderWindowNames,
  ]);

  const isPendingTabActive = activeTab === pendingTabIndex;
  allowedOrderWindowNames.some((name) =>
    [
      "Pending_NPD",
      "Pending_Rejected_NPD",
      "Pending_GlobalQuality",
      "Pending_Rejected_GlobalQuality",
      "Pending_Market_Proc",
      "Pending_Confirm_Price",
    ].includes(name)
  );

  if (isLoading) {
    return null;
  }


  return (
    <>
      <Drawer
        title="Create Campaign"
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        disableBackdropClick={true}
      >
        {createDialogOpen && (
          <CreateCampaignContainer
            onClose={() => setCreateDialogOpen(false)}
            onRefetchDraftCampaigns={() => {
              refetchDraftCampaigns();
            }}
          />
        )}
      </Drawer>

      <Card elevation={0} sx={{ p: 3, m: -3, mb: 3 }}>
        <Container maxWidth="xl">
          <Grid container justifyContent={"space-between"}>
            <Grid item xs={12} sx={{ display: "flex" }}>
              <TextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch} edge="end">
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size="small"
                label="Search"
                placeholder="What are you looking for?"
                variant="outlined"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  if (e.target.value === "") {
                    setSearchParams((oldParams) => {
                      let params = new URLSearchParams(oldParams);
                      params.delete("searchTerm");
                      return params;
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                sx={{ flex: "1 1 250px" }}
              />
            </Grid>
          </Grid>
        </Container>
      </Card>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Paper sx={{ overflow: "hidden" }}>
          <Box >

          </Box>
        </Paper>
      </Container>
      {currentUser.hasAboveMarketRole() &&
        hasPermission("Campaign.Create") && (
          <Fab
            color="primary"
            variant="extended"
            sx={{ position: "fixed", bottom: 50, right: 50 }}
            onClick={() => setCreateDialogOpen(true)}
          >
            <Campaign sx={{ mr: 1 }} />
            New Campaign
          </Fab>
        )}

      <Container maxWidth="xl">
        <Grid container>
          <Grid item xs={12}>
            <Tabs
              value={activeTab}

              onChange={(event, newValue) => {
                setActiveTab(newValue);

                const pendingTabIndex = getPendingTabIndex();

                if (newValue === pendingTabIndex) {
                  if (hasAllowedOrderWindow(CampaignState.Pending_NPD)) {
                    setSelectedPendingState(CampaignState.Pending_NPD);
                  } else if (
                    hasAllowedOrderWindow(CampaignState.Pending_Rejected_NPD)
                  ) {
                    setSelectedPendingState(CampaignState.Pending_Rejected_NPD);
                  } else if (
                    hasAllowedOrderWindow(CampaignState.Pending_GlobalQuality)
                  ) {
                    setSelectedPendingState(CampaignState.Pending_GlobalQuality);
                  } else if (
                    hasAllowedOrderWindow(
                      CampaignState.Pending_Rejected_GlobalQuality
                    )
                  ) {
                    setSelectedPendingState(
                      CampaignState.Pending_Rejected_GlobalQuality
                    );
                  } else if (
                    hasAllowedOrderWindow(CampaignState.Pending_Market_Proc)
                  ) {
                    setSelectedPendingState(CampaignState.Pending_Market_Proc);
                  } else if (
                    hasAllowedOrderWindow(CampaignState.Pending_Confirm_Price)
                  ) {
                    setSelectedPendingState(CampaignState.Pending_Confirm_Price);
                  } else {
                    setSelectedPendingState(null);
                  }
                } else {
                  setSelectedPendingState(null);
                }
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              scrollButtons="auto"
              aria-label="scrollable auto tabs example"
            >
              {[
                ...(currentUser.hasAboveMarketRole() &&
                  hasAllowedOrderWindow(CampaignState.Draft)
                  ? [[draftCampaigns, "Draft"]]
                  : []),

                ...(allowedOrderWindowNames.length > 0
                  ? [[{ total: getPendingTotalCount() }, "Pending"]]
                  : []),

                ...(hasAllowedOrderWindow(CampaignState.Closed_Submit_PR)
                  ? [[closedSubmitPrCampaigns, "Closed - Submit PR"]]
                  : []),
                ...(currentUser.hasAboveMarketRole() &&
                  hasAllowedOrderWindow(CampaignState.Archived)
                  ? [[archivedCampaigns, "Archived"]]
                  : []),
              ].map(([campaigns, label], index) => {
                const isPendingTab = label === "Pending";

                const displayLabel = `${label} Campaigns (${(campaigns as any)?.total || 0})`;

                return (
                  <Tab
                    key={index}
                    sx={{ fontSize: 18 }}
                    label={displayLabel}
                  />
                );
              })}
            </Tabs>

            {isPendingTabActive && selectedPendingState && (
              <Tabs
                value={selectedPendingState}
                onChange={(e, newValue) => {
                  setSelectedPendingState(newValue as CampaignState);
                }}
                variant="fullWidth"
                sx={{ mt: 1 }}
              >
                {hasAllowedOrderWindow(CampaignState.Pending_NPD) && (
                  <Tab
                    label={`Pending NPD (${pendingNpdCampaigns?.total || 0})`}
                    value={CampaignState.Pending_NPD}
                  />
                )}

                {hasAllowedOrderWindow(CampaignState.Pending_Rejected_NPD) && (
                  <Tab
                    label={`Pending - Rejected NPD (${pendingRejectedNpdCampaigns?.total || 0})`}
                    value={CampaignState.Pending_Rejected_NPD}
                  />
                )}

                {hasAllowedOrderWindow(CampaignState.Pending_GlobalQuality) && (
                  <Tab
                    label={`Pending - Global Quality (${pendingGlobalQualityCampaigns?.total || 0})`}
                    value={CampaignState.Pending_GlobalQuality}
                  />
                )}

                {hasAllowedOrderWindow(CampaignState.Pending_Rejected_GlobalQuality) && (
                  <Tab
                    label={`Pending - Rejected Global Quality (${pendingRejectedGlobalQualityCampaigns?.total || 0})`}
                    value={CampaignState.Pending_Rejected_GlobalQuality}
                  />
                )}

                {hasAllowedOrderWindow(CampaignState.Pending_Market_Proc) && (
                  <Tab
                    label={`Pending - Market Proc (${pendingMarketProcCampaigns?.total || 0})`}
                    value={CampaignState.Pending_Market_Proc}
                  />
                )}

                {hasAllowedOrderWindow(CampaignState.Pending_Confirm_Price) && (
                  <Tab
                    label={`Pending - Confirm Price (${pendingConfirmPriceCampaigns?.total || 0})`}
                    value={CampaignState.Pending_Confirm_Price}
                  />
                )}
              </Tabs>
            )}
          </Grid>
        </Grid>
      </Container>
      <Divider />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {(!currentTabData?.campaigns?.data || currentTabData.campaigns.data.length === 0) && (
          <Typography variant="h5" align="center">
            <div>
              <NothingFoundIcon style={{ maxWidth: 200 }} />
            </div>
            No Campaigns were found for the given criteria.
          </Typography>
        )}
        <Grid container spacing={4} alignItems={"stretch"}>
          {currentTabData?.campaigns?.data?.map((campaign, index) => (
            <Grid item xs={12} md={6} lg={4} xl={3} key={campaign.id}>
              <CampaignCard
                sx={{ height: "100%" }}
                onRefetch={() => refetchPastCampaigns()}
                refetchDraftCampaigns={refetchDraftCampaigns}
                key={campaign.id}
                {...campaign}
                imageUrl={
                  campaign.mediaFiles
                    .filter((file) => file.mimeType.indexOf("image/") > -1)
                    .sort(function (a, b) {
                      return (
                        new Date(a.uploadedOn).getTime() -
                        new Date(b.uploadedOn).getTime()
                      );
                    })[0]?.url
                }
              />
            </Grid>
          ))}
        </Grid>

        {/* Pagination for current tab */}
        {currentTabData?.campaigns?.total > 0 && (
          <Grid container sx={{ mt: 3 }}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" width="100%">
                <TablePagination
                  component="div"
                  count={currentTabData.campaigns.total}
                  page={currentTabData.pagination.pageIndex}
                  rowsPerPageOptions={[12, 24, 48, 96]}
                  onPageChange={(e, page) => {
                    currentTabData.setPagination({
                      ...currentTabData.pagination,
                      pageIndex: page,
                    });
                  }}
                  rowsPerPage={currentTabData.pagination.pageSize}
                  onRowsPerPageChange={(e) => {
                    currentTabData.setPagination({
                      ...currentTabData.pagination,
                      pageSize: parseInt(e.target.value, 10),
                      pageIndex: 0,
                    });
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
};

export default CampaignsListContainer;
